<template>
  <div>
    <div class="mfg-header">
      <h4 class="mfg-title">Traceability</h4>
    </div>
    <p class="text-muted small">Search a supplier lot, an internal lot or a production order number. Forward trace shows every batch and customer a lot reached (mock recall); backward trace shows where a finished batch came from.</p>

    <form class="filter-bar" @submit.prevent="search">
      <input class="form-control" v-model="q" placeholder="e.g. KSM-2026-114 or PRD-202610-0003" style="flex:1" />
      <button class="btn btn-primary" :disabled="q.length < 2 || loading">Search</button>
    </form>

    <div v-if="results.length" class="list-group mb-3">
      <button v-for="r in results" :key="r.id" class="list-group-item list-group-item-action d-flex justify-content-between" @click="trace(r)">
        <span><strong>{{ r.lot_number }}</strong> · {{ r.product }}<span v-if="r.supplier_lot_number" class="text-muted"> · supplier lot {{ r.supplier_lot_number }}</span></span>
        <span class="badge align-self-center" :class="r.status === 'rejected' ? 'badge-danger' : r.status === 'released' ? 'badge-primary' : 'badge-secondary'">{{ r.status }}</span>
      </button>
    </div>
    <p v-else-if="searched && !loading" class="text-muted">No lots match.</p>

    <div v-if="lot">
      <div class="d-flex align-items-center mb-2" style="gap:8px">
        <h5 class="mb-0">{{ lot.lot_number }} <small class="text-muted">{{ lot.product }}</small></h5>
        <div class="btn-group btn-group-toggle ml-auto">
          <button class="btn" :class="direction === 'forward' ? 'btn-primary' : 'btn-outline-primary'" @click="load('forward')">Forward</button>
          <button class="btn" :class="direction === 'backward' ? 'btn-primary' : 'btn-outline-primary'" @click="load('backward')">Backward</button>
        </div>
      </div>

      <div v-if="loading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div>

      <div v-else-if="direction === 'forward' && forward">
        <div class="row mb-4 pb-2 mfg-kpis">
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Production orders</div><div class="font-weight-bold" style="font-size:17px">{{ forward.production_orders.length }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Affected batches</div><div class="font-weight-bold" style="font-size:17px">{{ forward.affected_batches.length }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Customers reached</div><div class="font-weight-bold" style="font-size:17px">{{ forward.customers.length }}</div></div>
          <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Sold, no customer recorded</div><div class="font-weight-bold" style="font-size:17px" :class="forward.unidentified_sales_quantity > 0 ? 'text-danger' : ''">{{ qty(forward.unidentified_sales_quantity) }}</div></div>
        </div>

        <h6 class="section-title">Production orders that used this lot</h6>
        <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0"><thead><tr><th>Order</th><th>Used</th><th>Output</th><th>Batch</th><th>Completed</th></tr></thead>
          <tbody>
            <tr v-for="o in forward.production_orders" :key="o.production_order_id"><td>{{ o.order_number }}</td><td>{{ qty(o.quantity_of_lot_used) }}</td><td>{{ o.output_product }}</td><td>{{ o.output_lot || '—' }}</td><td class="small">{{ o.completed_at || '—' }}</td></tr>
            <tr v-if="!forward.production_orders.length"><td colspan="5" class="text-muted">Not used in production.</td></tr>
          </tbody>
        </table></div>

        <h6 class="section-title">Deliveries and sales</h6>
        <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0"><thead><tr><th>Batch</th><th>Customer</th><th>Document</th><th class="text-right">Qty (net of returns)</th><th>Date</th></tr></thead>
          <tbody>
            <tr v-for="(s, i) in forward.sales" :key="i"><td>{{ s.lot }}</td><td :class="!s.customer ? 'text-danger' : ''">{{ s.customer || 'No customer on sale' }}</td><td class="small">{{ s.document }}</td><td class="text-right">{{ qty(s.quantity) }}</td><td class="small">{{ s.date }}</td></tr>
            <tr v-if="!forward.sales.length"><td colspan="5" class="text-muted">Nothing sold or dispatched from this lot or its batches.</td></tr>
          </tbody>
        </table></div>
      </div>

      <div v-else-if="direction === 'backward' && backward">
        <trace-node :node="backward.tree" :depth="0"></trace-node>
      </div>
    </div>
  </div>
</template>

<script>
import { fmtQty } from './mfgHelpers';
import TraceNode from './TraceNode.vue';


export default {
  name: 'TraceabilityPanel',
  components: { TraceNode },
  data() {
    return { q: '', results: [], searched: false, loading: false, lot: null, direction: 'forward', forward: null, backward: null };
  },
  methods: {
    qty: fmtQty,
    async search() {
      this.loading = true; this.searched = true;
      try { this.results = (await axios.get('/api/manufacturing/trace/search', { params: { q: this.q } })).data; } finally { this.loading = false; }
    },
    /** Called from the lots panel and from search results. */
    trace(lot) {
      this.lot = { id: lot.id, lot_number: lot.lot_number, product: lot.product && lot.product.product ? lot.product.product : lot.product };
      this.forward = this.backward = null;
      this.load('forward');
    },
    async load(direction) {
      this.direction = direction;
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/manufacturing/lots/${this.lot.id}/trace/${direction}`);
        this[direction] = data;
      } finally { this.loading = false; }
    },
  },
};
</script>
